import { useEffect, useRef, useState } from "react";
import {
  Award,
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  Handshake,
  MapPin,
  MessageCircle,
  Menu,
  ShieldBan,
  Trophy,
  UserCheck,
  UserMinus,
  UserPlus,
  UserRound,
  Users,
  Waves,
} from "lucide-react";
import API_BASE from "../../api";
import AppBackButton from "../common/AppBackButton";
import {
  getBadgeKeyForRankName,
  getRankNameForLevel,
} from "../profile-progression/useProfileProgressionView";

const BADGE_BASE_PATH = "/badges/";
const BADGE_EXTENSION = ".png";

function normalizeProfileCoverPosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, parsed));
}

function normalizeProfileAvatarPosition(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 50;
  return Math.min(100, Math.max(0, parsed));
}

function resolveProfileAvatarObjectPosition(targetUser) {
  const positionX = normalizeProfileAvatarPosition(targetUser?.avatarPositionX);
  const positionY = normalizeProfileAvatarPosition(targetUser?.avatarPositionY);
  return `${positionX}% ${positionY}%`;
}

function resolveProfileCoverBackgroundPosition(targetUser) {
  const positionX = normalizeProfileCoverPosition(targetUser?.coverPositionX);
  const positionY = normalizeProfileCoverPosition(targetUser?.coverPositionY);
  return `${100 - positionX}% ${100 - positionY}%`;
}

function normalizePublicProfileText(value) {
  if (value === null || value === undefined) return "";
  return String(value).trim();
}

function hasPublicProfileValue(value) {
  const normalized = normalizePublicProfileText(value);
  return Boolean(normalized) && normalized !== "-";
}

function humanizePublicProfileValue(value) {
  return normalizePublicProfileText(value)
    .replace(/^[^.]+\./, "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function translatePublicProfileValue(t, value, fallback = "") {
  const normalized = normalizePublicProfileText(value);
  if (!normalized) return "";

  return t(normalized, {
    defaultValue: fallback || humanizePublicProfileValue(normalized),
  });
}

function resolvePublicProfileAccountType(profileDetails) {
  const rawType = normalizePublicProfileText(
    profileDetails?.accountType ||
      profileDetails?.userType ||
      profileDetails?.profileType
  ).toLowerCase();

  if (rawType.includes("business")) return "Business";
  if (rawType.includes("personal")) return "Personal";

  if (
    profileDetails?.business === true ||
    normalizePublicProfileText(profileDetails?.businessType) ||
    normalizePublicProfileText(profileDetails?.businessArea)
  ) {
    return "Business";
  }

  return "";
}

function resolvePublicProfileLocation(profileDetails) {
  const city = normalizePublicProfileText(profileDetails?.city);
  const country = normalizePublicProfileText(profileDetails?.country);

  return [city, country].filter(Boolean).join(", ");
}

function buildPublicProfilePreferenceChips(t, profileDetails) {
  const chips = [];
  const seen = new Set();
  const addChip = (key, label) => {
    const normalizedLabel = normalizePublicProfileText(label);
    if (!normalizedLabel) return;
    const normalizedKey = normalizePublicProfileText(key || normalizedLabel).toLowerCase();
    if (!normalizedKey || seen.has(normalizedKey)) return;
    seen.add(normalizedKey);
    chips.push({ key: normalizedKey, label: normalizedLabel });
  };
  const waterType = normalizePublicProfileText(profileDetails?.waterType).toLowerCase();
  const fishingStyle = normalizePublicProfileText(profileDetails?.fishingStyle).toLowerCase();

  if (waterType === "freshwater") {
    addChip("waterType:freshwater", t("freshwater", { defaultValue: "Freshwater" }));
  } else if (waterType === "saltwater") {
    addChip("waterType:saltwater", t("saltwater", { defaultValue: "Saltwater" }));
  } else if (hasPublicProfileValue(profileDetails?.waterType)) {
    addChip(
      `waterType:${profileDetails.waterType}`,
      translatePublicProfileValue(t, profileDetails.waterType)
    );
  }

  if (fishingStyle === "shore") {
    addChip("fishingStyle:shore", t("shore", { defaultValue: "Shore" }));
  } else if (fishingStyle === "boat") {
    addChip("fishingStyle:boat", t("boat", { defaultValue: "Boat" }));
  } else if (fishingStyle === "both") {
    addChip(
      "fishingStyle:both",
      t("profile_way_shore_boat", { defaultValue: "Shore & Boat" })
    );
  } else if (hasPublicProfileValue(profileDetails?.fishingStyle)) {
    addChip(
      `fishingStyle:${profileDetails.fishingStyle}`,
      translatePublicProfileValue(t, profileDetails.fishingStyle)
    );
  }

  if (Array.isArray(profileDetails?.methods)) {
    profileDetails.methods
      .map((method) => normalizePublicProfileText(method))
      .filter(Boolean)
      .forEach((method) => {
        addChip(`method:${method}`, translatePublicProfileValue(t, method));
      });
  }

  return chips;
}

function parsePublicProfileDate(value) {
  if (value === null || value === undefined || value === "") return null;

  const numericValue =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim()
      ? Number(value)
      : Number.NaN;

  const date = Number.isFinite(numericValue)
    ? new Date(numericValue < 1e12 ? numericValue * 1000 : numericValue)
    : new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
}

function formatPublicProfileJoinedDate(value) {
  const date = parsePublicProfileDate(value);
  if (!date) return "";

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatPublicProfileNumber(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return "\u2014";
  return new Intl.NumberFormat().format(parsed);
}

function buildPublicBadgeUrl(badgeKey) {
  const normalizedKey = normalizePublicProfileText(badgeKey).toLowerCase();
  if (!normalizedKey) return "";
  const publicUrl = process.env.PUBLIC_URL || "";
  return `${publicUrl}${BADGE_BASE_PATH}${normalizedKey}${BADGE_EXTENSION}`;
}

function getPublicProfilePositiveNumber(...candidates) {
  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
}

function getPublicProfileLevel(progression, profileDetails) {
  const level = getPublicProfilePositiveNumber(
    progression?.currentLevel,
    progression?.level,
    progression?.progressionLevel,
    profileDetails?.currentLevel,
    profileDetails?.level,
    profileDetails?.progressionLevel
  );

  return level ? Math.max(1, Math.floor(level)) : null;
}

function getPublicRankNameForLevel(level) {
  if (!level) return "";
  return getRankNameForLevel(level);
}

function getPublicRankNameCandidate(value) {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return normalizePublicProfileText(
      value.currentRankName || value.rankName || value.name || value.title || value.label
    );
  }

  const normalized = normalizePublicProfileText(value);
  if (
    !normalized ||
    /^[#\s]*\d+$/u.test(normalized) ||
    /^\d+(?:st|nd|rd|th)$/iu.test(normalized) ||
    /^(?:no\.?|rank)\s*#?\s*\d+$/iu.test(normalized)
  ) {
    return "";
  }
  return normalized;
}

function getPublicProgressionRankName(progression, profileDetails, level) {
  const candidates = [
    progression?.currentRankName,
    progression?.rankName,
    progression?.currentRank,
    progression?.rank,
    progression?.userRank,
    progression?.ranking,
    profileDetails?.currentRankName,
    profileDetails?.rankName,
    profileDetails?.currentRank,
    profileDetails?.rank,
    profileDetails?.userRank,
    profileDetails?.ranking,
  ];

  for (const candidate of candidates) {
    const rankName = getPublicRankNameCandidate(candidate);
    if (rankName) return rankName;
  }

  return getPublicRankNameForLevel(level);
}

function getPublicProgressionBadgeKey(progression, profileDetails, rankName) {
  const candidates = [
    progression?.currentBadgeKey,
    progression?.badgeKey,
    progression?.currentBadge,
    progression?.badge,
    profileDetails?.currentBadgeKey,
    profileDetails?.badgeKey,
    profileDetails?.currentBadge,
    profileDetails?.badge,
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      const objectKey = normalizePublicProfileText(
        candidate.key || candidate.badgeKey || candidate.slug || candidate.name
      );
      if (objectKey) return objectKey;
      continue;
    }

    const badgeKey = normalizePublicProfileText(candidate);
    if (badgeKey) return badgeKey;
  }

  return getBadgeKeyForRankName(rankName);
}

function getPublicRankPosition(profileDetails) {
  const progression = profileDetails?.progression || {};
  const candidates = [
    progression.globalRankPosition,
    progression.globalRank,
    progression.rankPosition,
    progression.publicRank,
    profileDetails?.globalRankPosition,
    profileDetails?.globalRank,
    profileDetails?.rankPosition,
    profileDetails?.publicRank,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
}

function formatPublicProfileDetailList(t, value) {
  const values = Array.isArray(value)
    ? value
    : normalizePublicProfileText(value)
    ? normalizePublicProfileText(value).split(",")
    : [];

  return values
    .map((item) => normalizePublicProfileText(item))
    .filter(Boolean)
    .map((item) => translatePublicProfileValue(t, item))
    .join(", ");
}

function buildPublicProfileBioRows(t, profileDetails, publicAccountType) {
  const rawAccountType =
    publicAccountType || normalizePublicProfileText(profileDetails?.accountType);
  const rows = [
    {
      label: t("profile_bio_city_label", { defaultValue: "City" }),
      value: normalizePublicProfileText(profileDetails?.city),
    },
    {
      label: t("profile_bio_country_label", { defaultValue: "Country" }),
      value: normalizePublicProfileText(profileDetails?.country),
    },
    {
      label: t("profile_bio_account_type_label", { defaultValue: "Account type" }),
      value: translatePublicProfileValue(t, rawAccountType),
    },
    {
      label: t("profile_bio_experience_label", { defaultValue: "Experience" }),
      value: translatePublicProfileValue(t, profileDetails?.experience),
    },
    {
      label: t("profile_bio_fishing_style_label", { defaultValue: "Fishing style" }),
      value: translatePublicProfileValue(t, profileDetails?.fishingStyle),
    },
    {
      label: t("profile_bio_water_type_label", { defaultValue: "Water type" }),
      value: translatePublicProfileValue(t, profileDetails?.waterType),
    },
    {
      label: t("profile_bio_range_type_label", { defaultValue: "Fishing range" }),
      value: translatePublicProfileValue(t, profileDetails?.rangeType),
    },
    {
      label: t("profile_bio_methods_label", { defaultValue: "Fishing methods" }),
      value: formatPublicProfileDetailList(t, profileDetails?.methods),
    },
    {
      label: t("profile_bio_species_label", { defaultValue: "Favorite species" }),
      value: formatPublicProfileDetailList(t, profileDetails?.favoriteSpecies),
    },
  ];

  return rows.filter((row) => hasPublicProfileValue(row.value));
}

function PublicProfileBioText({ bio, t }) {
  const textRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [isExpandable, setIsExpandable] = useState(false);

  useEffect(() => {
    setExpanded(false);
    setIsExpandable(false);
  }, [bio]);

  useEffect(() => {
    if (expanded) return undefined;

    const requestFrame =
      typeof window !== "undefined" && typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame
        : (callback) => window.setTimeout(callback, 0);
    const cancelFrame =
      typeof window !== "undefined" && typeof window.cancelAnimationFrame === "function"
        ? window.cancelAnimationFrame
        : window.clearTimeout;

    const frameId = requestFrame(() => {
      const element = textRef.current;
      if (!element) return;
      setIsExpandable(element.scrollHeight > element.clientHeight + 1);
    });

    return () => cancelFrame(frameId);
  }, [bio, expanded]);

  if (!hasPublicProfileValue(bio)) {
    return (
      <p className="userprofile-public-bio-text userprofile-public-bio-text--empty">
        {t("profile_public_no_bio", { defaultValue: "No bio yet" })}
      </p>
    );
  }

  return (
    <>
      <p
        ref={textRef}
        className={[
          "userprofile-public-bio-text",
          expanded ? "userprofile-public-bio-text--expanded" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {bio}
      </p>
      {isExpandable ? (
        <button
          type="button"
          className="userprofile-public-bio-toggle"
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded
            ? t("show_less", { defaultValue: "Show less" })
            : t("read_more", { defaultValue: "Read more" })}
        </button>
      ) : null}
    </>
  );
}

export default function UserProfileHeaderSection({ profile, onOpenSharedSidebar }) {
  const {
    t,
    selectedUser,
    selectedUserId,
    selectedUserData,
    friendStatus,
    friendRequestPending,
    isFollowing,
    followAllowed,
    followersCount,
    followingCount,
    followActionPending,
    blockStatus,
    hasBlockedUser,
    blockActionPending,
    blockFeedback,
    blockError,
    canManageBlock,
    confirmAction,
    friendsCount,
    backLabel,
    handleCloseUserProfile,
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    cancelFriendRequest,
    handleFriendButtonClick,
    handleFollowButtonClick,
    handleMessageClick,
    handleBlockButtonClick,
    handleOpenExpandedMedia,
  } = profile;

  const avatarSrc = selectedUserData?.avatar
    ? `${API_BASE}${selectedUserData.avatar}?t=${selectedUserData.updatedAt || Date.now()}`
    : "https://cdn-icons-png.flaticon.com/512/847/847969.png";

  const coverSrc = selectedUserData?.cover
    ? `${API_BASE}${selectedUserData.cover}?t=${selectedUserData.updatedAt || Date.now()}`
    : null;
  const bioSummaryRef = useRef(null);
  const [activeSummaryInfo, setActiveSummaryInfo] = useState(null);
  const publicAccountType = resolvePublicProfileAccountType(selectedUserData);
  const publicLocation = resolvePublicProfileLocation(selectedUserData);
  const publicJoinedDate = formatPublicProfileJoinedDate(
    selectedUserData?.createdAt ||
      selectedUserData?.created_at ||
      selectedUserData?.joinedAt ||
      selectedUserData?.dateJoined
  );
  const publicInfoItems = [
    publicAccountType
      ? {
          key: "account-type",
          icon:
            publicAccountType === "Business" ? BriefcaseBusiness : UserRound,
          label: publicAccountType,
        }
      : null,
    publicLocation
      ? {
          key: "location",
          icon: MapPin,
          label: publicLocation,
        }
      : null,
    publicJoinedDate
      ? {
          key: "joined",
          icon: CalendarDays,
          label: t("profile_joined_date", {
            defaultValue: "Joined {{date}}",
            date: publicJoinedDate,
          }),
        }
      : null,
  ].filter(Boolean);
  const publicPreferenceChips = buildPublicProfilePreferenceChips(t, selectedUserData);
  const progression = selectedUserData?.progression || {};
  const currentLevel = getPublicProfileLevel(progression, selectedUserData);
  const currentRankName = getPublicProgressionRankName(
    progression,
    selectedUserData,
    currentLevel
  );
  const currentBadgeUrl = buildPublicBadgeUrl(
    getPublicProgressionBadgeKey(progression, selectedUserData, currentRankName)
  );
  const rankPosition = getPublicRankPosition(selectedUserData);
  const formattedRankPosition = rankPosition
    ? t("profile_global_rank_value", {
        defaultValue: "No. {{rank}}",
        rank: formatPublicProfileNumber(rankPosition),
      })
    : t("progression_rank_default", { defaultValue: "Unranked" });
  const formattedPublicLevel = currentRankName || "\u2014";
  const normalizedPublicBio = normalizePublicProfileText(selectedUserData?.bio);
  const publicBioRows = buildPublicProfileBioRows(
    t,
    selectedUserData,
    publicAccountType
  );
  const summaryCards = [
    {
      key: "xp",
      icon: Trophy,
      label: t("progression_xp_label", { defaultValue: "XP" }),
      value: formatPublicProfileNumber(progression.xp),
      onClick: () => setActiveSummaryInfo("xp"),
      ariaLabel: t("progression_xp_button_label"),
    },
    {
      key: "level",
      icon: Award,
      label: t("progression_level_label", { defaultValue: "Level" }),
      value: formattedPublicLevel,
      onClick: () => setActiveSummaryInfo("level"),
      ariaLabel: t("progression_level_button_label"),
    },
    {
      key: "rank",
      icon: Users,
      label: t("profile_global_rank_label", { defaultValue: "Rank" }),
      value: formattedRankPosition,
      onClick: () => setActiveSummaryInfo("rank"),
      ariaLabel: t("progression_rank_button_label"),
    },
    {
      key: "bio",
      icon: BookOpen,
      label: t("profile_bio_text_label", { defaultValue: "Bio" }),
      value: t("profile_bio_text_label", { defaultValue: "Bio" }),
      onClick: () => setActiveSummaryInfo("bio"),
      ariaLabel: t("profile_bio_button_label"),
    },
  ];
  const socialCards = [
    {
      key: "followers",
      icon: Users,
      value: followersCount,
      label: t("followers"),
    },
    {
      key: "following",
      icon: UserCheck,
      value: followingCount,
      label: t("following"),
    },
    {
      key: "friends",
      icon: Handshake,
      value: friendsCount,
      label: t("friends"),
    },
  ];
  const summaryInfoModalCopy =
    activeSummaryInfo === "xp"
      ? {
          dialogLabel: t("progression_xp_popup_dialog_label"),
          kicker: t("progression_xp_popup_kicker"),
          title: t("progression_xp_popup_title"),
          body: t("progression_xp_popup_body"),
          bullets: [
            t("progression_xp_popup_detail_catches_trips"),
            t("progression_xp_popup_detail_catch_details"),
            t("progression_xp_popup_detail_engagement"),
            t("progression_xp_popup_detail_checklists"),
          ],
          pill: t("progression_xp_popup_pill", {
            xp: formatPublicProfileNumber(progression.xp),
          }),
        }
      : activeSummaryInfo === "level"
      ? {
          dialogLabel: t("progression_level_popup_dialog_label"),
          kicker: t("progression_level_popup_kicker"),
          title: t("progression_level_popup_title"),
          body: t("progression_level_popup_body"),
          bullets: [
            t("progression_level_popup_detail_xp"),
            t("progression_level_popup_detail_activity"),
            t("progression_level_popup_detail_long_term"),
          ],
          pill: formattedPublicLevel,
        }
      : activeSummaryInfo === "rank"
      ? {
          dialogLabel: t("profile_global_rank_popup_dialog_label"),
          kicker: t("profile_global_rank_popup_kicker"),
          title: t("profile_global_rank_popup_title"),
          body: t("profile_global_rank_popup_body"),
          bullets: [
            t("profile_global_rank_popup_detail_xp"),
            t("profile_global_rank_popup_detail_leaderboard"),
            t("profile_global_rank_popup_detail_unranked"),
          ],
          pill: formattedRankPosition,
        }
      : activeSummaryInfo === "bio"
      ? {
          dialogLabel: t("profile_bio_modal_dialog_label", {
            defaultValue: "Profile bio",
          }),
          kicker: t("profile_bio_modal_kicker", { defaultValue: "About" }),
          title: t("profile_bio_text_label", { defaultValue: "Bio" }),
          bioRow: normalizedPublicBio
            ? {
                label: t("profile_bio_text_label", { defaultValue: "Bio" }),
                value: normalizedPublicBio,
              }
            : null,
          rows: publicBioRows,
          emptyMessage: t("profile_bio_modal_empty", {
            defaultValue: "No profile information available yet.",
          }),
        }
      : null;

  useEffect(() => {
    if (!activeSummaryInfo) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActiveSummaryInfo(null);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [activeSummaryInfo]);

  return (
    <>
      <div className="userprofile-mobile-topchrome rp-mobile-topchrome">
        <div className="userprofile-mobile-navbar rp-navbar">
          {onOpenSharedSidebar ? (
            <button
              type="button"
              className="userprofile-mobile-topchrome__menu rp-navbar__menu-btn mobile-menu-btn"
              aria-label={t("open_menu", { defaultValue: "Open menu" })}
              onClick={onOpenSharedSidebar}
            >
              <Menu size={23} strokeWidth={2.25} aria-hidden="true" />
            </button>
          ) : null}

          <div className="userprofile-mobile-topchrome__logo rp-navbar__logo">
            <img
              src="/banner/logo.png"
              alt={t("app_title")}
              className="userprofile-mobile-topchrome__logo-img rp-navbar__logo-img"
            />
            <div className="userprofile-mobile-topchrome__wordmark userprofile-mobile-topchrome__username rp-navbar__wordmark">
              {selectedUserData?.username || selectedUser || t("app_title")}
            </div>
          </div>
        </div>
      </div>

      <div className="userprofile-header-wrapper">
        <AppBackButton
          className="userprofile-back"
          label={backLabel}
          onClick={handleCloseUserProfile}
        />

        <div className="userprofile-avatar-wrapper">
          <img
            src={avatarSrc}
            alt={t("profile_picture")}
            className="userprofile-avatar clickable"
            style={{
              objectPosition: resolveProfileAvatarObjectPosition(selectedUserData),
              transform: `scale(${selectedUserData?.avatarScale || 1})`,
            }}
            onClick={() =>
              handleOpenExpandedMedia({
                type: "image",
                src: avatarSrc,
              })
            }
          />
        </div>

        <h2 className="userprofile-floating-name">
          {selectedUserData?.username || selectedUser}
        </h2>

        <div
          className={`userprofile-cover ${selectedUserData?.cover ? "has-cover" : ""}`}
          style={
            selectedUserData?.cover
              ? {
                  backgroundImage: `url(${coverSrc})`,
                  backgroundPosition:
                    resolveProfileCoverBackgroundPosition(selectedUserData),
                  backgroundSize: `${100 * (selectedUserData?.coverScale || 1)}%`,
                }
              : undefined
          }
          onClick={() => {
            if (coverSrc) {
              handleOpenExpandedMedia({
                type: "image",
                src: coverSrc,
              });
            }
          }}
        />
      </div>

      {publicInfoItems.length > 0 ? (
        <div
          className="userprofile-public-info-row"
          aria-label={t("profile_public_details_aria", {
            defaultValue: "Public profile details",
          })}
        >
          {publicInfoItems.map((item) => {
            const InfoIcon = item.icon;
            return (
              <span className="userprofile-public-info-pill" key={item.key}>
                <InfoIcon size={15} strokeWidth={2.25} aria-hidden="true" />
                <span>{item.label}</span>
              </span>
            );
          })}
        </div>
      ) : null}

      <div className="userprofile-actions">
        {friendStatus === "loading" && (
          <button disabled>
            <UserRound
              className="userprofile-action-icon"
              size={17}
              strokeWidth={2.25}
              aria-hidden="true"
            />
            <span>{t("loading")}</span>
          </button>
        )}

        {friendStatus === "none" && (
          <button onClick={sendFriendRequest} disabled={friendRequestPending}>
            <UserPlus
              className="userprofile-action-icon"
              size={17}
              strokeWidth={2.25}
              aria-hidden="true"
            />
            <span>{t("add_friend")}</span>
          </button>
        )}

        {friendStatus === "requested" && (
          <button onClick={cancelFriendRequest} disabled={friendRequestPending}>
            <UserMinus
              className="userprofile-action-icon"
              size={17}
              strokeWidth={2.25}
              aria-hidden="true"
            />
            <span>
              {friendRequestPending
                ? t("loading")
                : t("cancel_request", { defaultValue: "Cancel request" })}
            </span>
          </button>
        )}

        {friendStatus === "received" && (
          <>
            <button onClick={acceptFriendRequest}>
              <UserCheck
                className="userprofile-action-icon"
                size={17}
                strokeWidth={2.25}
                aria-hidden="true"
              />
              <span>{t("accept")}</span>
            </button>
            <button onClick={declineFriendRequest}>
              <UserMinus
                className="userprofile-action-icon"
                size={17}
                strokeWidth={2.25}
                aria-hidden="true"
              />
              <span>{t("decline")}</span>
            </button>
          </>
        )}

        {friendStatus === "friends" && (
          <button onClick={handleFriendButtonClick}>
            <UserCheck
              className="userprofile-action-icon"
              size={17}
              strokeWidth={2.25}
              aria-hidden="true"
            />
            <span>{t("friends")}</span>
          </button>
        )}

        {followAllowed && (
          <button onClick={handleFollowButtonClick} disabled={followActionPending}>
            {isFollowing ? (
              <UserCheck
                className="userprofile-action-icon"
                size={17}
                strokeWidth={2.25}
                aria-hidden="true"
              />
            ) : (
              <UserPlus
                className="userprofile-action-icon"
                size={17}
                strokeWidth={2.25}
                aria-hidden="true"
              />
            )}
            <span>{isFollowing ? t("following") : t("follow")}</span>
          </button>
        )}

        <button
          onClick={() => {
            if (!selectedUserId) return;
            handleMessageClick();
          }}
        >
          <MessageCircle
            className="userprofile-action-icon"
            size={17}
            strokeWidth={2.25}
            aria-hidden="true"
          />
          <span>{t("message")}</span>
        </button>

        {canManageBlock ? (
          <button
            type="button"
            className="userprofile-block-action"
            onClick={handleBlockButtonClick}
            disabled={blockStatus !== "ready" || blockActionPending}
          >
            <ShieldBan
              className="userprofile-action-icon"
              size={17}
              strokeWidth={2.25}
              aria-hidden="true"
            />
            <span>
              {blockStatus === "loading"
                ? t("loading")
                : hasBlockedUser
                  ? t("user_profile_unblock_user", {
                      defaultValue: "Unblock user",
                    })
                  : t("user_profile_block_user", {
                      defaultValue: "Block user",
                    })}
            </span>
          </button>
        ) : null}
      </div>

      {blockFeedback || (blockError && confirmAction !== "block") ? (
        <p
          className={`userprofile-block-feedback ${
            blockError && confirmAction !== "block"
              ? "userprofile-block-feedback--error"
              : ""
          }`}
          role={blockError && confirmAction !== "block" ? "alert" : "status"}
        >
          {(confirmAction !== "block" && blockError) || blockFeedback}
        </p>
      ) : null}

      <div className="userprofile-public-overview">
        {publicPreferenceChips.length > 0 ? (
          <div
            className="userprofile-public-preferences"
            aria-label={t("profile_preferences", { defaultValue: "Fishing preferences" })}
          >
            {publicPreferenceChips.map((chip) => (
              <span className="userprofile-public-preference-chip" key={chip.key}>
                <Waves size={13} strokeWidth={2.2} aria-hidden="true" />
                <span>{chip.label}</span>
              </span>
            ))}
          </div>
        ) : null}

        <div className="userprofile-public-stats-card">
          <div
            className="userprofile-public-summary-grid"
            aria-label={t("profile_public_summary", { defaultValue: "Public profile summary" })}
          >
            {summaryCards.map((card) => {
              const CardIcon = card.icon;
              const content = (
                <>
                  <span className="userprofile-public-summary-card__icon" aria-hidden="true">
                    <CardIcon size={19} strokeWidth={2.25} />
                  </span>
                  <span className="userprofile-public-summary-card__label">
                    {card.label}
                  </span>
                  <strong className="userprofile-public-summary-card__value">
                    {card.value}
                  </strong>
                </>
              );

              return card.onClick ? (
                <button
                  type="button"
                  className={`userprofile-public-summary-card userprofile-public-summary-card--${card.key}`}
                  key={card.key}
                  onClick={card.onClick}
                  aria-label={card.ariaLabel}
                >
                  {content}
                </button>
              ) : (
                <div
                  className={`userprofile-public-summary-card userprofile-public-summary-card--${card.key}`}
                  key={card.key}
                >
                  {content}
                </div>
              );
            })}
          </div>

          <section
            className="userprofile-public-bio-card"
            ref={bioSummaryRef}
            aria-label={t("profile_public_bio_summary", { defaultValue: "Public profile bio summary" })}
          >
            <div className="userprofile-public-bio-card__badge">
              {currentBadgeUrl ? (
                <img
                  src={currentBadgeUrl}
                  alt={
                    currentRankName
                      ? t("profile_rank_badge_alt", {
                          defaultValue: "{{rankName}} badge",
                          rankName: currentRankName,
                        })
                      : ""
                  }
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <Award size={34} strokeWidth={2.1} aria-hidden="true" />
              )}
              {currentRankName ? <span>{currentRankName}</span> : null}
            </div>
            <div className="userprofile-public-bio-card__copy">
              <span className="userprofile-public-bio-card__eyebrow">
                {t("profile_bio_text_label", { defaultValue: "Bio" })}
              </span>
              <PublicProfileBioText
                bio={normalizePublicProfileText(selectedUserData?.bio)}
                t={t}
              />
            </div>
          </section>
        </div>

        <div
          className="userprofile-public-social-grid"
          aria-label={t("profile_social_stats", { defaultValue: "Profile social stats" })}
        >
          {socialCards.map((card) => {
            const SocialIcon = card.icon;
            return (
              <div className="userprofile-public-social-card" key={card.key}>
                <span className="userprofile-public-social-card__icon" aria-hidden="true">
                  <SocialIcon size={20} strokeWidth={2.2} />
                </span>
                <strong>{formatPublicProfileNumber(card.value)}</strong>
                <span>{card.label}</span>
              </div>
            );
          })}
        </div>
      </div>

      {summaryInfoModalCopy ? (
        <div
          className="profile-progression-modal-overlay userprofile-summary-info-overlay"
          onClick={() => setActiveSummaryInfo(null)}
        >
          <div
            className="profile-progression-modal userprofile-summary-info-modal"
            role="dialog"
            aria-modal="true"
            aria-label={summaryInfoModalCopy.dialogLabel}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="profile-progression-modal-header-actions">
              <button
                type="button"
                className="profile-progression-modal-close"
                onClick={() => setActiveSummaryInfo(null)}
              >
                {t("progression_milestone_popup_close")}
              </button>
            </div>

            <div className="profile-progression-modal-kicker">
              {summaryInfoModalCopy.kicker}
            </div>
            <h3 className="profile-progression-modal-title">
              {summaryInfoModalCopy.title}
            </h3>
            {summaryInfoModalCopy.body ? (
              <p className="profile-progression-modal-body">
                {summaryInfoModalCopy.body}
              </p>
            ) : null}
            {summaryInfoModalCopy.bullets?.length ? (
              <ul className="profile-progression-modal-list">
                {summaryInfoModalCopy.bullets.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
            {summaryInfoModalCopy.rows ? (
              summaryInfoModalCopy.bioRow || summaryInfoModalCopy.rows.length ? (
                <div className="profile-progression-modal-details profile-progression-modal-details--bio">
                  {summaryInfoModalCopy.bioRow ? (
                    <div className="profile-progression-modal-detail profile-progression-modal-detail--bio-primary">
                      <span className="profile-progression-modal-detail-label">
                        {summaryInfoModalCopy.bioRow.label}
                      </span>
                      <p className="profile-progression-modal-detail-value">
                        {summaryInfoModalCopy.bioRow.value}
                      </p>
                    </div>
                  ) : null}

                  {summaryInfoModalCopy.rows.length ? (
                    <div className="profile-progression-modal-details-grid">
                      {summaryInfoModalCopy.rows.map((row) => (
                        <div
                          key={row.label}
                          className="profile-progression-modal-detail"
                        >
                          <span className="profile-progression-modal-detail-label">
                            {row.label}
                          </span>
                          <p className="profile-progression-modal-detail-value">
                            {row.value}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="profile-progression-modal-empty">
                  {summaryInfoModalCopy.emptyMessage}
                </p>
              )
            ) : null}
            {summaryInfoModalCopy.pill ? (
              <div className="profile-progression-modal-pill">
                {summaryInfoModalCopy.pill}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
